import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Typo } from '@curry-group/mui-curcuma';
import { GeneralModal } from '../../components/modals/GeneralModal';
import { createComplaintRequest } from '../../data/actions/botChat';
import { BOT_CREATED_BY } from '../../data/reducer/botChat';
import { getDetailRoute } from '../../helper';
import { IMessageModel } from '../../model/communication/Message';
import { ThingTypes } from '../../model/ryve/Thing';
import { BOT_CHAT_DISCLAIMER } from './infoButton';

const COMPLAINT_HINT = 'Sie haben keine passende Antwort auf ihre Frage erhalten? Wenden Sie sich an ';

/**
 * Last question/answer pair of a bot conversation — what a complaint is about.
 */
export function lastBotTurn(messages: IMessageModel[]): { question: string; answer: string } {
  let answer = '';
  let question = '';
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    const isBot = message.createdBy === BOT_CREATED_BY;
    if (!answer && isBot) {
      answer = message.content || '';
      continue;
    }
    if (answer && !isBot) {
      question = message.content || '';
      break;
    }
  }
  return { question, answer };
}

/**
 * Disclaimer under the bot chat title. When a complaint contact is configured it
 * ends in a link that opens a chat with that person, seeded with the bot turn the
 * user is unhappy about. Without a contact the link is left out entirely rather
 * than pointing nowhere.
 */
export const BotChatDisclaimer: React.FC<{ messages: IMessageModel[]; sessionId?: string }> = ({ messages, sessionId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const contact = useSelector(state => state.foundation?.complaintContact);
  const creating = useSelector(state => state.botChat?.complaintCreating) || false;
  const createdComplaint = useSelector(state => state.botChat?.createdComplaint);
  const failed = useSelector(state => state.botChat?.complaintFailed);
  const appconfig = useSelector(state => state.foundation?.appconfig);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const turn = useMemo(() => lastBotTurn(messages), [messages]);
  // The link shows whenever a contact is configured; before the bot has answered
  // there is nothing to send yet, so the dialog explains that instead of opening.
  const showLink = !!contact?.userId;
  const hasTurn = !!turn.answer;

  useEffect(() => {
    if (!createdComplaint?.alias) return;
    // A complaint chat is an ordinary 1:1 conversation, so let the normal route
    // resolution place it — hand-picking a container got it wrong (the first one
    // with a sidebar is Communities, not Chat).
    const route = getDetailRoute(
      { typeId: ThingTypes.OneToOne, alias: createdComplaint.alias },
      appconfig || {}
    );
    if (route) history.push(route);
  }, [createdComplaint, appconfig, history]);

  const submit = () => {
    if (!hasTurn) return;
    setConfirmOpen(false);
    dispatch(createComplaintRequest({ botSessionId: sessionId, question: turn.question, answer: turn.answer }));
  };

  return (
    <>
      <Typo variant="body2" color="textSecondary">
        {BOT_CHAT_DISCLAIMER}
        {showLink && (
          <>
            {' '}
            {COMPLAINT_HINT}
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={creating}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                textDecoration: 'underline',
                cursor: creating ? 'default' : 'pointer',
              }}
            >
              {creating ? 'die Fachredaktion von Medtec Online (wird geöffnet …)' : 'die Fachredaktion von Medtec Online'}
            </button>
            .
          </>
        )}
        {failed && (
          <>
            {' '}
            <span>Der Chat konnte nicht geöffnet werden. Bitte versuchen Sie es später erneut.</span>
          </>
        )}
      </Typo>

      <GeneralModal
        open={confirmOpen}
        headline="Betreiber kontaktieren"
        content={
          hasTurn
            ? 'Um Sie besser unterstützen zu können, werden Ihre letzte Frage und Antwort an Dr. Oliver Bujok, als Betreiber, übermittelt. Möchten Sie mit diesen Angaben den Chat öffnen?'
            : 'Sie müssen zuerst eine Frage stellen, bevor Sie den Betreiber kontaktieren können.'
        }
        commitText="Chat öffnen"
        commitDisabled={!hasTurn}
        cancelText="Abbrechen"
        close={() => setConfirmOpen(false)}
        cancel={() => setConfirmOpen(false)}
        commit={submit}
      />
    </>
  );
};
